import { useState, useRef, useEffect } from 'react';
import { getCardioVideo } from '@/utils/cardioVideos';

/**
 * CardioMedia — drop-in replacement for cardio <img> elements.
 * If a video exists for the given modality_id, renders a muted looping video.
 * Otherwise (or on video error), renders the existing image.
 * All className / style props are passed through unchanged.
 */
export function CardioMedia({ modalityId, modalityName, src, alt, className, videoClassName, style, loading, eager }) {
  const videoUrl = getCardioVideo(modalityId, modalityName);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  // Reset failure state if the video URL changes
  useEffect(() => { setVideoFailed(false); }, [videoUrl]);

  // Nudge playback on mount / URL change (WebView can ignore autoPlay after resume)
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play?.().catch(() => {});
  }, [videoUrl, videoFailed]);

  if (videoUrl && !videoFailed) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        className={videoClassName || className}
        style={style}
        autoPlay
        loop
        muted
        playsInline
        preload={eager ? 'auto' : 'metadata'}
        poster={src || undefined}
        onError={() => setVideoFailed(true)}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt || ''}
      className={className}
      style={style}
      loading={eager ? 'eager' : (loading || 'lazy')}
    />
  );
}
